import React from 'react';
import { LocalServicePage } from '../../components/LocalServicePage';

export const LocksmithBarking = () => {
  return (
    <LocalServicePage
      areaName="Barking"
      areaType="City"
      slug="locksmith-barking"
      heroTitle="Door, Window & Lock Repairs in Barking"
      heroSubtitle="Royal Security provides specialist door, window, and lock repair services across Barking. From riverside apartments to traditional terraces, we deliver honest diagnosis and quality repairs."
      introContent={
        <>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Barking is experiencing significant change, with major regeneration alongside established residential areas. This mix means varied property types and door/window systems—from the traditional housing around Barking town centre to the new-build apartments along the Roding and towards Barking Riverside.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            We work across all of Barking, adapting our expertise to each property type. Whether you need repairs on a Victorian terrace door, a 1960s flat entrance, or a contemporary apartment system, we have the knowledge and parts to help.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            Our service covers homeowners, landlords, letting agents, and property managers. Barking's strong rental sector means we frequently assist with tenant changeovers, urgent repairs, and ongoing maintenance—always with honest pricing and reliable workmanship.
          </p>
        </>
      }
      servicesEmphasis={[
        {
          title: "Multipoint Lock Repairs",
          description: "Modern doors across Barking use multipoint locking systems. We diagnose faults in gearboxes, hooks, rollers, and cylinders, repairing or replacing components as needed to restore secure, smooth operation."
        },
        {
          title: "Flat & Apartment Door Systems",
          description: "Barking has many purpose-built flats with specific entrance door systems. We work on communal door hardware, flat entrance locks, and intercom-linked systems—understanding the particular requirements of these installations."
        },
        {
          title: "uPVC Door & Window Repairs",
          description: "uPVC remains common throughout Barking's housing. We repair all aspects—mechanisms, handles, hinges, seals, and hardware. Our repairs extend the life of your existing installations."
        },
        {
          title: "Tilt & Turn Windows",
          description: "Many Barking flats feature tilt and turn windows for ventilation and fire safety. When mechanisms fail, we repair the hardware—tilt restrictors, turn mechanisms, handles, and locking points."
        },
        {
          title: "Glazing Services",
          description: "Misted double glazing, cracked panes, and failed sealed units are problems we resolve regularly. We replace glass within existing frames and offer upgrade options for improved performance."
        },
        {
          title: "Emergency & Urgent Repairs",
          description: "When security is compromised or a door won't lock, you need prompt attention. We prioritise genuine emergencies and provide realistic timeframes for urgent work."
        }
      ]}
      localContextTitle="Flat Doors & Tenant Changeovers in Barking: What to Check First"
      localContext={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Flat entrance doors:</strong> If a self-closing flat door won't latch on its own, the cause needs assessing—it could be the closer, latch, keep, hinges, or the door or frame having moved. Many flat entrance doors are fire doors, so any adjustment or replacement part must not compromise fire performance, including intumescent strips and seals. Tell us if the door has a fire-door label or certificate, and whether your building manager needs to approve the work.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Tenant changeovers:</strong> For landlords and agents, a cylinder change is often enough rather than a full lock replacement, depending on the lock fitted. Let us know how many keys you need cut, whether communal or window keys are also involved, and who will be on site to give access.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            <strong>Before calling, check:</strong> Does the key turn but the door won't open, or does the handle lift but the key won't turn? Is the problem only when the door is shut? These details help narrow down whether the cylinder, gearbox or alignment is involved, so we can bring likely parts.
          </p>
        </>
      }
      commonProblems={[
        {
          title: "Flat door not closing on latch",
          description: "Striker plate alignment or latch mechanism issues. We adjust to ensure doors close and lock securely."
        },
        {
          title: "Window won't tilt or turn correctly",
          description: "Mechanism problems specific to tilt & turn windows. We repair or replace the faulty hardware."
        },
        {
          title: "Multipoint lock stiff or jamming",
          description: "Could be worn gearbox, misalignment, or component failure. Diagnosis identifies the exact cause."
        },
        {
          title: "Draught around door frame",
          description: "Seal degradation or adjustment issues. Usually resolved with new seals and realignment."
        },
        {
          title: "Double glazing condensation",
          description: "Sealed unit failure. We replace the glass unit while keeping your existing frame."
        },
        {
          title: "Handle broken or mechanism stuck",
          description: "We repair or replace handles and internal mechanisms to restore smooth operation."
        }
      ]}
      whyChooseUs={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            We operate across Barking and understand the varied needs of properties here. From urgent repairs in rental flats to careful work on period homes, we adapt our service while maintaining consistent quality.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Our repair-first approach is particularly relevant in an area with diverse budgets. If your door can be fixed affordably, that's what we'll recommend. We're not interested in selling expensive replacements to people who don't need them.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            For landlords and letting agents working in Barking, we provide efficient, reliable service. Quick turnaround for tenant issues, honest assessments for property purchases, and ongoing maintenance that prevents problems before they become urgent.
          </p>
        </>
      }
      ctaText="Need Door or Window Repairs in Barking?"
    />
  );
};
